import { useEffect, useState, useMemo } from 'react';
import { groupBy } from 'lodash';

import { iconToCompanyName, LLM_MODEL_DEFAULT } from '@/constants';
import loadIcon from '@/helpers/loadIcons';
import { getLLMModels } from '@/services/model.service';
import { ListboxOption } from '@headlessui/react';
import { updateChatbotLLMModel } from '@/services/chatbot.service';
import { updateDeployment } from '@/services/deployment.service';

import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useUserStore } from '@/stores/user/userStore';
import { useParams } from 'react-router-dom';
import useModalStore from '@/stores/modal/modalStore';
import Select from '@/shared/components/UI/Select';
import OpenAiLogoIcon from '@/shared/icons/OpenAiLogoIcon';
import DSpinner from '@/components/Global/DSpinner';
import IconWrapper from '@/shared/components/UI/IconWrapper';

const LLMSelectorSimple = ({
  label = '',
  deploymentId = null,
  currentModelType = null,
  selectedColor = 'bg-v3-black-placeholder',
  listButtonClass = '',
  listButtonClassName = '',
  showCredits = true,
  dropdownClassName = '',
  chevronClassName = '',
  onModelChange,
  shouldHaveDropdownWidth = true,
  dropdownWidth = null,
  fullWidth = false,
  helperText,
  error,
  canHaveError = true,
}) => {
  const params = useParams();
  const { user } = useUserStore();
  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const [selectedLLM, setSelectedLLM] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const setSelectedLLModel = useChatbotStore(
    (state) => state.setSelectedLLModel
  );
  const llmModels = useChatbotStore((state) => state.llmModels);
  const llmModelsLastFetchedTier = useChatbotStore((state) => state.llmModelsLastFetchedTier);
  const setLLMModels = useChatbotStore((state) => state.setLLMModels);

  const [modelsByCompany, setModelsByCompany] = useState({});
  const [icons, setIcons] = useState({});
  const [iconsLoaded, setIconsLoaded] = useState(false);

  // Fetch LLM models directly from /llms API (cached via Zustand store)
  // Use owner's tier for team members to get the correct models
  const effectiveTierType = user?.owner_tier_type || user?.tier_type;

  useEffect(() => {
    if (
      effectiveTierType &&
      (llmModels.length === 0 || llmModelsLastFetchedTier !== effectiveTierType)
    ) {
      const fetchModels = async () => {
        try {
          const response = await getLLMModels({ tier_type: effectiveTierType });
          setLLMModels(response.data || [], effectiveTierType);
        } catch (error) {
          console.error('Failed to fetch LLM models:', error);
          setLLMModels([], effectiveTierType);
        }
      };
      fetchModels();
    }
  }, [effectiveTierType, llmModels.length, llmModelsLastFetchedTier, setLLMModels]);

  const models = llmModels;

  // Memoized helper function to get the correct icon component for a model
  const getModelIcon = useMemo(() => {
    return (model) => {
      if (!model?.Logo) return OpenAiLogoIcon;
      return icons[model.Logo] || OpenAiLogoIcon;
    };
  }, [icons]);

  const handleChangeModel = async (modelValue) => {
    // Find the model object from the models array
    const model = models.find(m => m.value === modelValue);
    if (!model) return;

    // Check if model is disabled based on backend availability
    const isModelDisabled = !model.available;

    if (isModelDisabled) {
      // Show upgrade modal with model name
      const dynamicFeature = `llm_model:${model.label}`;
      useModalStore.getState().openPlansModal(dynamicFeature);
      return;
    }

    setSelectedLLM(model);

    try {
      setIsLoading(true);

      // If deploymentId is provided, update the deployment's model
      if (deploymentId) {
        await updateDeployment(deploymentId, params.id, {
          model_type: model.value,
        });

        // Notify parent component if callback provided
        if (onModelChange) {
          onModelChange(model);
        }
      } else {
        // Otherwise, update the chatbot's default model
        setSelectedLLModel(model);
        await updateChatbotLLMModel({
          kb_id: params?.id,
          llmModel: model.value
        });
      }
    } catch (e) {
      console.error('Failed to update LLM model', e);
      // Revert on error
      const previousModel = deploymentId
        ? models.find(m => m.value === currentModelType)
        : selectedChatbot?.last_model_used;
      setSelectedLLM(previousModel || LLM_MODEL_DEFAULT);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadModels = async () => {
      const groupedModels = groupBy(models, 'Logo');

      const iconComponents = {};

      for (const company of Object.keys(groupedModels)) {
        try {
          const iconCompany = await loadIcon(company);
          iconComponents[company] = iconCompany;
        } catch (e) {
          console.error(`Failed to load icon ${company}`, e);
        }
      }

      setIcons(iconComponents);
      setModelsByCompany(groupedModels);
      setIconsLoaded(true);
    };
    if (models?.length) {
      loadModels();
    }
  }, [models]);

  // Set initial selected model for deployment rows (deployment-specific)
  useEffect(() => {
    if (deploymentId && currentModelType && models?.length > 0) {
      const initialModel = models.find(m => m.value === currentModelType);
      setSelectedLLM(initialModel || LLM_MODEL_DEFAULT);
    }
  }, [models, deploymentId, currentModelType]);

  // Set initial selected model for chatbot-level selector (only when NOT a deployment)
  useEffect(() => {
    if (!deploymentId && models?.length > 0) {
      const initialModel = selectedChatbot?.last_model_used;
      setSelectedLLM(initialModel || LLM_MODEL_DEFAULT);
    }
  }, [models, deploymentId, selectedChatbot?.last_model_used]);

  // Ensure we always have a valid icon component
  const IconComponent = getModelIcon(selectedLLM);

  // Additional safety check - ensure selectedLLM has proper structure
  const safeSelectedLLM = selectedLLM || LLM_MODEL_DEFAULT;

  return (
    <Select
      size="md"
      label={label}
      value={safeSelectedLLM.value}
      onChange={handleChangeModel}
      helperText={helperText}
      error={error}
      selectedChild={
        !models || models.length === 0 || !iconsLoaded ? (
          <div className="flex items-center justify-start gap-v3-xs text-xs">
            <DSpinner style={{ height: 22, width: 22 }} />
          </div>
        ) : (
          <div className="flex items-center gap-v3-xxs">
            <IconWrapper size={16}>
              <IconComponent className="w-full h-full" />
            </IconWrapper>
            <span className="text-v3-black-max text-[15px] truncate w-[180px] font-normal leading-6 ">
              {safeSelectedLLM.label}
            </span>
          </div>
        )
      }
      listButtonClassName={listButtonClassName || `flex items-center justify-between gap-v3-xxs !py-v3-xs !px-v3-ms self-stretch ${listButtonClass}`}
      className="w-full"
      optionContainerClassName="flex-col items-start"
      selectClassName="!p-0"
      optionsClassName={`shadow-v3-card ${dropdownClassName}`}
      chevronIconClassName={chevronClassName}
      disabled={isLoading}
      dropdownWidth={dropdownWidth || (fullWidth ? '680px' : (shouldHaveDropdownWidth ? '216px' : 'auto'))}
      canHaveError={canHaveError}
      wrapperClassName="w-full"
    >
      {Object.keys(modelsByCompany)?.map((company) => {
        const Icon = icons[company];

        return (
          <div key={company} className="flex flex-col gap-v3-xxs w-full">
            <span className="text-v3-black-max text-v3-h10 font-medium px-v3-ms my-v3-xs">
              {iconToCompanyName[company]}
            </span>
            {modelsByCompany[company].map((model) => {
              const isModelDisabled = !model.available;
              const isSelected = selectedLLM?.value === model.value;

              return (
                <ListboxOption
                  as="button"
                  key={model.value}
                  value={model.value}
                  disabled={isModelDisabled}
                  className={`flex items-center gap-v3-xs text-v3-h10 px-v3-ms py-[8px] cursor-pointer w-full  hover:bg-v3-menu-selected ${isSelected ? 'bg-v3-menu-selected' : ''} ${isModelDisabled ? 'opacity-50 cursor-default' : ''}`}
                  onClick={(e) => {
                    if (isModelDisabled) {
                      e.preventDefault();
                      e.stopPropagation();

                      // Show upgrade modal with model name
                      const dynamicFeature = `llm_model:${model.label}`;
                      useModalStore.getState().openPlansModal(dynamicFeature);
                      return false;
                    }
                  }}
                >
                  {Icon && <IconWrapper size={20}><Icon className="text-v3-black-max" /></IconWrapper>}
                  <div className='flex flex-col items-start flex-1 text-v3-black-max'>
                    <span className='text-v3-black-max text-v3-h10 font-medium'>{model.label}</span>
                    {showCredits && (
                      <span className='text-v3-black-max text-v3-h10'>
                        {model.credits} credits per response
                      </span>
                    )}
                  </div>
                 
                </ListboxOption>
              );
            })}
          </div>
        );
      })}
    </Select>
  );
};

export default LLMSelectorSimple;

